import { AxiosRequestConfig, AxiosResponse } from 'axios'
import { apiService } from '../../api-base'
import {
  AvailableProductModel,
  AvailableProductDetailModel,
  Brand,
  CreateFeatureModel,
  CreateMaterialModel,
  EditableProductModel,
  Feature,
  Items,
  Manufacturer,
  Material,
  Option,
  Product,
  ShippingModel,
  SingleOptionModel,
  OptionsResponse,
  ProductOptionsApiResponse,
  ProductVariationByOptionsResponse,
  PayloadCreateBrand,
} from './types'
import { ProductOptionAvailability } from '../kits/types'

export class ProductsService {
  static getStoreProducts(
    storeId: string,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<Product[]>> {
    return apiService.get<AxiosResponse<Product[]>>(
      `/stores/${storeId}/products`,
      config,
    )
  }

  static getProducts(
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<Product[]>> {
    return apiService.get<AxiosResponse<Product[]>>(`/products`, config)
  }

  static getItems(id: string): Promise<AxiosResponse<Items>> {
    return apiService.get<AxiosResponse<Items>>(`/products/${id}/items`)
  }

  static getRequiredLocalization(): Promise<{ localizations: string[] }> {
    return apiService.get<{ localizations: string[] }>(
      '/users/required-localizations',
    )
  }

  static getProductById(
    id: string,
  ): Promise<AxiosResponse<EditableProductModel>> {
    return apiService.get<AxiosResponse<EditableProductModel>>(
      `/products/${id}/edit`,
    )
  }

  static createProduct(data?: unknown): Promise<AxiosResponse<{ id: string }>> {
    return apiService.post<AxiosResponse<{ id: string }>>(`/products`, data)
  }

  static getAvailableProducts(
    config: AxiosRequestConfig,
  ): Promise<AxiosResponse<AvailableProductModel[]>> {
    return apiService.get<AxiosResponse<AvailableProductModel[]>>(
      `/products/availabilities`,
      config,
    )
  }

  static getAvailableProduct(
    id: string,
  ): Promise<AxiosResponse<AvailableProductDetailModel>> {
    return apiService.get<AxiosResponse<AvailableProductDetailModel>>(
      `/products/available/${id}`,
    )
  }

  static updateProduct(
    id: string,
    data?: unknown,
  ): Promise<AxiosResponse<{ id: string }>> {
    return apiService.put<AxiosResponse<{ id: string }>>(
      `/products/available/${id}`,
      data,
    )
  }

  static getOptions(
    config: AxiosRequestConfig,
  ): Promise<AxiosResponse<Option[]>> {
    return apiService.get<AxiosResponse<Option[]>>('/options', config)
  }

  static getOptionById(id: string): Promise<AxiosResponse<SingleOptionModel>> {
    return apiService.get<AxiosResponse<SingleOptionModel>>(`/options/${id}`)
  }

  static getMaterials(
    config: AxiosRequestConfig,
  ): Promise<AxiosResponse<Material[]>> {
    return apiService.get<AxiosResponse<Material[]>>('/materials', config)
  }

  static addMaterial(
    data: CreateMaterialModel,
  ): Promise<AxiosResponse<Material>> {
    return apiService.post<AxiosResponse<Material>>('/materials', data)
  }

  static getFeatures(
    config: AxiosRequestConfig,
  ): Promise<AxiosResponse<Feature[]>> {
    return apiService.get<AxiosResponse<Feature[]>>('/features', config)
  }

  static addFeature(data: CreateFeatureModel): Promise<AxiosResponse<Feature>> {
    return apiService.post<AxiosResponse<Feature>>('/features', data)
  }

  static generateBarcode(
    id: string,
    payload: { options: Record<string, string> },
  ): Promise<AxiosResponse<Blob>> {
    return apiService.postBlob(`/products/${id}/generateBarcode`, payload)
  }

  static getBrands(
    config: AxiosRequestConfig,
  ): Promise<AxiosResponse<Brand[]>> {
    return apiService.get<AxiosResponse<Brand[]>>('/brands', config)
  }

  static getShipping(): Promise<AxiosResponse<ShippingModel[]>> {
    return apiService.get<AxiosResponse<ShippingModel[]>>('/shipping')
  }

  static hideProductOption(data: {
    productId: string
    optionId: string
    optionValueId: string
  }): Promise<AxiosResponse<unknown>> {
    return apiService.post<AxiosResponse<unknown>>(
      `/products/${data.productId}/options/${data.optionId}/hide`,
      {
        default_option_value_id: data.optionValueId,
      },
    )
  }

  static returnOption(data: {
    productId: string
    optionId: string
  }): Promise<AxiosResponse<SingleOptionModel>> {
    return apiService.delete<AxiosResponse<SingleOptionModel>>(
      `/products/${data.productId}/options/${data.optionId}/return`,
    )
  }

  static getFilterByName<T>(
    name: string,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<T[]>> {
    return apiService.get<AxiosResponse<T[]>>(`/${name}`, config)
  }

  static getOptionsDetail(id: string): Promise<AxiosResponse<OptionsResponse>> {
    return apiService.get<AxiosResponse<OptionsResponse>>(
      `products/${id}/options-detail`,
    )
  }

  static testGet(id: string): Promise<AxiosResponse<OptionsResponse>> {
    return apiService.get<AxiosResponse<OptionsResponse>>(
      `/products/${id}/options`,
    )
  }

  static getProductOptions(
    productId: string,
  ): Promise<ProductOptionsApiResponse> {
    return apiService.get<ProductOptionsApiResponse>(
      `/products/${productId}/options`,
    )
  }

  static getAvailableProductVariation(
    productId: string,
    config?: AxiosRequestConfig,
  ): Promise<ProductVariationByOptionsResponse> {
    return apiService.get<ProductVariationByOptionsResponse>(
      `/products/available/${productId}/variation`,
      config,
    )
  }

  static clonedProduct(
    productId: string,
    data: {
      storeId: string
    },
  ): Promise<AxiosResponse<Material>> {
    return apiService.post<AxiosResponse<Material>>(
      `/products/${productId}/clone`,
      data,
    )
  }

  static arhiveProduct(id: string): Promise<AxiosResponse<{ id: string }>> {
    return apiService.put<AxiosResponse<{ id: string }>>(
      `/products/${id}/archive`,
    )
  }

  static createManufacturers(data: {
    name: string
  }): Promise<AxiosResponse<Manufacturer>> {
    return apiService.post<AxiosResponse<Manufacturer>>('/manufacturers', data)
  }

  static createBrand(data: PayloadCreateBrand): Promise<AxiosResponse<Brand>> {
    return apiService.post<AxiosResponse<Brand>>('/brands', data)
  }

  static getProductOptionsAvailability(
    productAvailabilityId: string,
  ): Promise<AxiosResponse<ProductOptionAvailability[]>> {
    return apiService.get(
      `products/${productAvailabilityId}/options-availability`,
    )
  }
}
